import {
  CATEGORIAS_SERVICIO,
  MONEDA,
  OBJETO_IMP,
  entradasOrdenadas,
  servicioCreateSchema,
} from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import type { Servicio } from "../lib/servicios";
import { AreaTexto, Aviso, Boton, Campo, Entrada, Seleccion } from "./ui";

/** Unidades del SAT que cubren prácticamente todo lo que facturamos. */
const UNIDADES = [
  { clave: "E48", nombre: "Servicio" },
  { clave: "ACT", nombre: "Actividad" },
  { clave: "HUR", nombre: "Hora" },
  { clave: "DAY", nombre: "Día" },
  { clave: "MON", nombre: "Mes" },
  { clave: "H87", nombre: "Pieza" },
];

/** Tasas de IVA vigentes. La región fronteriza usa 8%. */
const TASAS_IVA = [
  { valor: "0.160000", texto: "16% (general)" },
  { valor: "0.080000", texto: "8% (región fronteriza)" },
  { valor: "0.000000", texto: "0% (tasa cero)" },
  { valor: "", texto: "Exento" },
];

type Campos = {
  clave: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  claveProdServ: string;
  claveUnidad: string;
  precioBase: string;
  moneda: string;
  objetoImp: string;
  tasaIva: string;
  esRecurrente: boolean;
  notas: string;
};

const vacio: Campos = {
  clave: "",
  nombre: "",
  descripcion: "",
  categoria: "DESARROLLO",
  claveProdServ: "81111500",
  claveUnidad: "E48",
  precioBase: "",
  moneda: "MXN",
  objetoImp: "02",
  tasaIva: "0.160000",
  esRecurrente: false,
  notas: "",
};

const desdeServicio = (s: Servicio): Campos => ({
  clave: s.clave,
  nombre: s.nombre,
  descripcion: s.descripcion,
  categoria: s.categoria,
  claveProdServ: s.claveProdServ,
  claveUnidad: s.claveUnidad,
  precioBase: s.precioBase ?? "",
  moneda: s.moneda,
  objetoImp: s.objetoImp,
  tasaIva: s.tasaIva ?? "",
  esRecurrente: s.esRecurrente,
  notas: s.notas ?? "",
});

interface Props {
  servicio?: Servicio;
  /** Categoría con la que abre el formulario de alta: la pestaña visible. */
  categoriaInicial?: string;
  enviando?: boolean;
  errorServidor?: string | null;
  alEnviar: (datos: Record<string, unknown>) => void;
  alCancelar: () => void;
}

export function FormularioServicio({
  servicio,
  categoriaInicial,
  enviando,
  errorServidor,
  alEnviar,
  alCancelar,
}: Props) {
  const [campos, setCampos] = useState<Campos>(
    servicio
      ? desdeServicio(servicio)
      : { ...vacio, categoria: categoriaInicial ?? vacio.categoria },
  );
  const [errores, setErrores] = useState<Record<string, string>>({});

  const set = <K extends keyof Campos>(llave: K) => (valor: Campos[K]) => {
    setCampos((previos) => ({ ...previos, [llave]: valor }));
    setErrores(({ [llave]: _, ...resto }) => resto);
  };

  function enviar(e: FormEvent) {
    e.preventDefault();

    const datos = {
      clave: campos.clave.trim().toUpperCase(),
      nombre: campos.nombre.trim(),
      descripcion: campos.descripcion.trim(),
      categoria: campos.categoria,
      claveProdServ: campos.claveProdServ.trim(),
      claveUnidad: campos.claveUnidad,
      unidad: UNIDADES.find((u) => u.clave === campos.claveUnidad)?.nombre,
      // Vacío significa "sin tarifa definida", no cero.
      precioBase: campos.precioBase.trim() || null,
      moneda: campos.moneda,
      objetoImp: campos.objetoImp,
      tasaIva: campos.tasaIva || null,
      esRecurrente: campos.esRecurrente,
      notas: campos.notas.trim() || undefined,
    };

    const resultado = servicioCreateSchema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    alEnviar(datos);
  }

  return (
    <form onSubmit={enviar} className="space-y-5 p-5">
      {errorServidor && <Aviso>{errorServidor}</Aviso>}

      <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
        <Campo
          etiqueta="Clave"
          requerido
          error={errores["clave"]}
          ayuda="Para buscarlo al cotizar"
        >
          <Entrada
            value={campos.clave}
            onChange={(e) => set("clave")(e.target.value.toUpperCase())}
            error={!!errores["clave"]}
            className="font-mono uppercase"
            placeholder="DEV-MEDIDA"
          />
        </Campo>

        <Campo etiqueta="Nombre" requerido error={errores["nombre"]}>
          <Entrada
            value={campos.nombre}
            onChange={(e) => set("nombre")(e.target.value)}
            error={!!errores["nombre"]}
          />
        </Campo>
      </div>

      <Campo
        etiqueta="Descripción"
        requerido
        error={errores["descripcion"]}
        ayuda="Se imprime tal cual en la factura: escríbela pensando en quien la lea meses después"
      >
        <AreaTexto
          rows={3}
          value={campos.descripcion}
          onChange={(e) => set("descripcion")(e.target.value)}
          error={!!errores["descripcion"]}
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Categoría" requerido error={errores["categoria"]}>
          <Seleccion
            value={campos.categoria}
            onChange={(e) => set("categoria")(e.target.value)}
          >
            {Object.entries(CATEGORIAS_SERVICIO).map(([clave, nombre]) => (
              <option key={clave} value={clave}>
                {nombre}
              </option>
            ))}
          </Seleccion>
        </Campo>

        <Campo etiqueta="Unidad" requerido error={errores["claveUnidad"]}>
          <Seleccion
            value={campos.claveUnidad}
            onChange={(e) => set("claveUnidad")(e.target.value)}
          >
            {UNIDADES.map((u) => (
              <option key={u.clave} value={u.clave}>
                {u.clave} · {u.nombre}
              </option>
            ))}
          </Seleccion>
        </Campo>
      </div>

      <fieldset className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Datos fiscales
        </legend>

        <div className="grid gap-4 sm:grid-cols-3">
          <Campo
            etiqueta="ClaveProdServ"
            requerido
            error={errores["claveProdServ"]}
            ayuda="8 dígitos del catálogo del SAT"
          >
            <Entrada
              value={campos.claveProdServ}
              onChange={(e) =>
                set("claveProdServ")(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              error={!!errores["claveProdServ"]}
              inputMode="numeric"
              className="font-mono"
            />
          </Campo>

          <Campo etiqueta="Objeto de impuesto" error={errores["objetoImp"]}>
            <Seleccion
              value={campos.objetoImp}
              onChange={(e) => set("objetoImp")(e.target.value)}
            >
              {entradasOrdenadas(OBJETO_IMP).map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="IVA" error={errores["tasaIva"]}>
            <Seleccion
              value={campos.tasaIva}
              onChange={(e) => set("tasaIva")(e.target.value)}
            >
              {TASAS_IVA.map((t) => (
                <option key={t.texto} value={t.valor}>
                  {t.texto}
                </option>
              ))}
            </Seleccion>
          </Campo>
        </div>

        <p className="text-xs text-slate-500">
          Las retenciones de ISR e IVA no se definen aquí: dependen de a quién
          se le factura —solo aplican de persona física a persona moral— y se
          resuelven al emitir el comprobante.
        </p>
      </fieldset>

      <fieldset className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-800">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tarifa
        </legend>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Precio base"
            error={errores["precioBase"]}
            ayuda="Déjalo vacío si el precio se define en cada cotización"
          >
            <Entrada
              value={campos.precioBase}
              onChange={(e) =>
                set("precioBase")(e.target.value.replace(/[^\d.]/g, ""))
              }
              error={!!errores["precioBase"]}
              inputMode="decimal"
              className="font-mono"
              placeholder="0.00"
            />
          </Campo>

          <Campo etiqueta="Moneda" error={errores["moneda"]}>
            <Seleccion
              value={campos.moneda}
              onChange={(e) => set("moneda")(e.target.value)}
            >
              {Object.entries(MONEDA)
                .filter(([clave]) => clave !== "XXX")
                .map(([clave, nombre]) => (
                  <option key={clave} value={clave}>
                    {clave} · {nombre}
                  </option>
                ))}
            </Seleccion>
          </Campo>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={campos.esRecurrente}
            onChange={(e) => set("esRecurrente")(e.target.checked)}
            className="rounded border-slate-300 text-marca-600 focus:ring-marca-500"
          />
          <span>
            Se cobra de forma recurrente
            <span className="ml-1 text-xs text-slate-500">
              (soporte, hospedaje, retainers)
            </span>
          </span>
        </label>
      </fieldset>

      <Campo etiqueta="Notas internas" error={errores["notas"]}>
        <AreaTexto
          rows={2}
          value={campos.notas}
          onChange={(e) => set("notas")(e.target.value)}
        />
      </Campo>

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
        <Boton type="button" variante="secundario" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : servicio ? "Guardar cambios" : "Crear servicio"}
        </Boton>
      </div>
    </form>
  );
}
